import React, { useEffect, useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import { azureSqlService } from '@/services/azureSqlService.js';
import { Button, Modal } from '@/ui/components';
import '../Sms/SmsSendModal.css';

export type EmailEntityType = 'task' | 'quotation' | 'invoice';

export type EmailModalTarget = {
  entityType: EmailEntityType;
  entityId: number;
  label: string;
};

type EmailPreviewRecipient = {
  key: string;
  name: string;
  email: string;
};

type EmailPreview = {
  entityType: EmailEntityType;
  entityId: number;
  entityLabel: string;
  message: string;
  subject: string;
  recipients: EmailPreviewRecipient[];
};

type EmailSendResult = {
  sentCount: number;
  failedCount: number;
  unknownCount?: number;
  reminderContact?: { state: string; reason: string | null; receiptRef: string };
  results: Array<{
    recipientKey: string;
    recipientName: string;
    successful: boolean;
    errorMessage?: string | null;
  }>;
};

type EmailSendModalProps = {
  open: boolean;
  target: EmailModalTarget | null;
  onClose: () => void;
};

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || 'Email request failed.');
}

export default function EmailSendModal({ open, target, onClose }: EmailSendModalProps) {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<EmailSendResult | null>(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (!open || !target) return undefined;
    let active = true;
    setLoading(true);
    setPreview(null);
    setSelectedKeys(new Set());
    setMessage('');
    setSubject('');
    setError(null);
    setSendResult(null);

    void azureSqlService.previewNotificationEmail({ entityType: target.entityType, entityId: target.entityId })
      .then((value: EmailPreview) => {
        if (!active) return;
        setPreview(value);
        setMessage(value.message);
        setSubject(value.subject);
        setSelectedKeys(new Set(value.recipients.map((recipient) => recipient.key)));
      })
      .catch((requestError: unknown) => {
        if (active) setError(errorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, target]);

  const selectedCount = selectedKeys.size;
  const allSelected = Boolean(preview?.recipients.length) && selectedCount === preview?.recipients.length;
  const messageIsEmpty = message.trim().length === 0;
  const messageIsInvalid = messageIsEmpty || message.trim().length > 20000;
  const subjectIsInvalid = !subject.trim() || subject.trim().length > 200 || /[\r\n]/.test(subject);
  const failedRecipientKeys = useMemo(
    () => new Set((sendResult?.results || []).filter((result) => !result.successful).map((result) => result.recipientKey)),
    [sendResult]
  );

  const toggleRecipient = (recipientKey: string) => {
    if (sending || sendResult) return;
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(recipientKey)) next.delete(recipientKey);
      else next.add(recipientKey);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview || sending || sendResult) return;
    setSelectedKeys(allSelected ? new Set() : new Set(preview.recipients.map((recipient) => recipient.key)));
  };

  const handleSend = async () => {
    if (!target || !preview || selectedCount === 0 || messageIsInvalid || subjectIsInvalid || sending || sendResult) return;
    setSending(true);
    setError(null);
    try {
      const result = await azureSqlService.sendNotificationEmail({
        entityType: target.entityType,
        entityId: target.entityId,
        recipientKeys: Array.from(selectedKeys),
        message: message.trim(),
        subject: subject.trim(),
      }) as EmailSendResult;
      setSendResult(result);
      if ((result.unknownCount || 0) > 0 || result.reminderContact?.state === 'unknown') {
        setError('The reminder submission or its saved receipt is unconfirmed. Check reminder history and Sent Items before taking further action. Do not resend or switch to SMS.');
      } else if (result.failedCount > 0) {
        const failures = result.results
          .filter((item) => !item.successful)
          .map((item) => `${item.recipientName}: ${item.errorMessage || 'Delivery failed'}`)
          .join(' ');
        setError(`${result.sentCount} sent, ${result.failedCount} failed. ${failures}`);
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
      if (target.entityType === 'invoice') {
        setSendResult({ sentCount: 0, failedCount: 0, unknownCount: selectedCount, results: [] });
        setError('The reminder outcome could not be confirmed. Check the original reminder history and Sent Items before taking further action. Do not resend or switch to SMS.');
      }
    } finally {
      setSending(false);
    }
  };

  const footer = sendResult && sendResult.failedCount === 0 ? (
    <Button type="button" onClick={onClose}>Close</Button>
  ) : (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>Cancel</Button>
      <Button
        type="button"
        onClick={() => void handleSend()}
        loading={sending}
        disabled={loading || !preview || selectedCount === 0 || messageIsInvalid || Boolean(subjectIsInvalid) || Boolean(sendResult)}
        iconBefore={<Mail aria-hidden="true" />}
      >
        {selectedCount > 1 ? `Send ${selectedCount} Emails` : 'Send Email'}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={sending ? () => undefined : onClose}
      title={`Send Email${target?.label ? ` - ${target.label}` : ''}`}
      footer={footer}
      size="md"
      className="sms-send-modal"
    >
      {loading ? <div className="sms-send-modal__state">Preparing message...</div> : null}

      {!loading && error ? (
        <div className="sms-send-modal__alert" role="alert">{error}</div>
      ) : null}

      {!loading && sendResult?.failedCount === 0 && !sendResult.unknownCount && sendResult.reminderContact?.state !== 'unknown' ? (
        <div className="sms-send-modal__success" role="status">
          Email submitted for {sendResult.sentCount} recipient{sendResult.sentCount === 1 ? '' : 's'}. Saved to the sender's Sent Items; delivery and reading are not confirmed.
        </div>
      ) : null}

      {!loading && preview ? (
        <div className="sms-send-modal__body">
          <section className="sms-send-modal__section" aria-labelledby="sms-recipients-title">
            <div className="sms-send-modal__section-heading">
              <h3 id="sms-recipients-title">Recipients</h3>
              {preview.recipients.length > 1 && !sendResult ? (
                <button type="button" className="sms-send-modal__select-all" onClick={toggleAll}>
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              ) : null}
            </div>
            <div className="sms-send-modal__recipients">
              {preview.recipients.map((recipient) => (
                <label
                  key={recipient.key}
                  className={`sms-send-modal__recipient${failedRecipientKeys.has(recipient.key) ? ' sms-send-modal__recipient--failed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(recipient.key)}
                    onChange={() => toggleRecipient(recipient.key)}
                    disabled={sending || Boolean(sendResult)}
                  />
                  <span>
                    <strong>{recipient.name}</strong>
                    <small>{recipient.email}</small>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="sms-send-modal__section">
            <label htmlFor="email-subject-editor">Subject</label>
            <input
              id="email-subject-editor"
              className="sms-send-modal__message"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              maxLength={200}
              disabled={sending || Boolean(sendResult)}
              aria-invalid={subjectIsInvalid ? 'true' : undefined}
            />
          </section>
          <section className="sms-send-modal__section" aria-labelledby="sms-message-title">
            <div className="sms-send-modal__section-heading">
              <h3 id="sms-message-title">Message</h3>
              <span
                className={`sms-send-modal__segment-count${messageIsInvalid ? ' sms-send-modal__segment-count--invalid' : ''}`}
                aria-live="polite"
              >
                {message.length.toLocaleString()} / 20,000 characters
              </span>
            </div>
            <textarea
              id="sms-message-editor"
              className={`sms-send-modal__message${messageIsInvalid ? ' sms-send-modal__message--invalid' : ''}`}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={8}
              disabled={sending || Boolean(sendResult)}
              aria-label="Email message"
              aria-invalid={messageIsInvalid ? 'true' : undefined}
              aria-describedby={messageIsInvalid ? 'sms-message-guidance' : undefined}
            />
            {messageIsInvalid ? (
              <p id="sms-message-guidance" className="sms-send-modal__message-guidance" role="alert">
                {messageIsEmpty
                  ? 'Enter a message before sending.'
                  : 'Keep the email within 20,000 characters.'}
              </p>
            ) : null}
          </section>
        </div>
      ) : null}
    </Modal>
  );
}
